import { Center, Spinner, Box, useToken } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";
import { useAdminQuery } from "utils/apollo/hooks/admin";
import usePrevious from "utils/usePrevious";
import UserError from "utils/UserError";
import { gql } from "__generated__/gql";
import { CONTENT_FULL_HEIGHT } from "components/Layout/constants";

const contentsSignedUrlQuery = gql(/* GraphQL */ `
  query SelectedEmailContentsSignedUrl($emailId: UUID!) {
    receivedEmailMessage(emailMessageId: $emailId) {
      emailMessageId
      parsedReceivedEmailMessage {
        receivedEmailMessageId
        parsedContentsFileUrl
      }
    }
  }
`);

type ParsedContent = {
  html?: string;
  textAsHtml: string;
};

type Props = {
  emailId: UUID;
};

export default function SelectedEmailContent({ emailId }: Props) {
  const { showErrorToast, showToast } = useAsyncCompleteToast();
  const [parsedContent, setParsedContent] = useState<ParsedContent>();
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const bodyColor = useToken("colors", "gray.800");

  const { data, loading: isLoadingUrl } = useAdminQuery(
    contentsSignedUrlQuery,
    {
      variables: { emailId },
      fetchPolicy: "network-only",
    },
  );

  useEffect(() => {
    showToast.closeAll();
  }, [emailId, showToast]);

  const { parsedContentsFileUrl: parsedContentsUrl } =
    data?.receivedEmailMessage?.parsedReceivedEmailMessage || {};
  const prevParsedContentsUrl = usePrevious(parsedContentsUrl);

  useEffect(() => {
    if (parsedContentsUrl && parsedContentsUrl !== prevParsedContentsUrl) {
      setIsLoadingContent(true);
      fetch(parsedContentsUrl)
        .then((result) => {
          if (!result.ok) {
            throw new UserError(
              "Received an unsuccessful response while getting the email's contents",
              { description: result.statusText },
            );
          }
          return result.json();
        })
        .then((json) => setParsedContent(json as ParsedContent))
        .catch((reason) => {
          showErrorToast(reason, {
            title: "An error occurred getting the email's contents",
          });
        })
        .finally(() => {
          setIsLoadingContent(false);
        });
    }
  }, [parsedContentsUrl, prevParsedContentsUrl, showErrorToast]);

  if (
    isLoadingUrl ||
    isLoadingContent ||
    !parsedContentsUrl ||
    typeof window === "undefined"
  ) {
    return (
      <Center minH="10rem">
        <Spinner size="xl" color="magenta.dark" />
      </Center>
    );
  }

  const htmlContent = parsedContent?.html || parsedContent?.textAsHtml;
  if (!htmlContent) {
    return (
      <Center minH="10rem">
        <Spinner size="xl" color="magenta.dark" />
      </Center>
    );
  }

  const iframeDocument = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <base target="_blank" />
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,300..900;1,300..900&display=swap" rel="stylesheet">
        <style>
          body {
            margin: 0;
            padding: 16px;
            font-family: 'Figtree', system-ui, sans-serif;
            color: ${bodyColor};
          }
          img {
            max-width: 100%;
            height: auto;
          }
        </style>
      </head>
      <body>${htmlContent}</body>
    </html>
  `;

  return (
    <Box
      as="iframe"
      srcDoc={iframeDocument}
      sandbox="allow-popups"
      width="100%"
      height={`calc(${CONTENT_FULL_HEIGHT} - 16rem)`}
      minH="15rem"
      title="Email content"
    />
  );
}
